"use client";

import React, { createContext, useContext } from "react";
import { VoiceModel } from "@/actions/db/get-models";
import { useQuery, useQueryClient } from "react-query";

export interface ITtsModelsContext {
  models: VoiceModel[];
  setModels: (value: VoiceModel[]) => void;
  isLoading: boolean;
}

const TtsModelsContext = createContext<ITtsModelsContext | null>(null);

export function useTtsModelsContext() {
  const ttsModelsContext = useContext(TtsModelsContext);

  if (!ttsModelsContext) {
    throw new Error("useTtsModelsContext must be used within a ModelsProvider");
  }

  return ttsModelsContext;
}

const TtsModelsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const queryClient = useQueryClient();

  const fetchTtsModels = async () => {
    const response = await fetch("/api/tts-model");
    return (await response.json()) as VoiceModel[];
  };

  const { data, isLoading } = useQuery(
    ["ttsVoiceModels"],
    () => fetchTtsModels(),
    {
      onError: (err) => {
        console.error("Error fetching tts models:", err);
      },
    },
  );

  const setModels = (value: VoiceModel[]) => {
    queryClient.setQueryData(["ttsVoiceModels"], value);
  };

  const modelsContextValue = {
    models: data ?? [],
    setModels: setModels,
    isLoading,
  };

  return (
    <TtsModelsContext.Provider value={modelsContextValue}>
      {children}
    </TtsModelsContext.Provider>
  );
};

export default TtsModelsProvider;
